// src/components/Layout/Sidebar/index.tsx
import { useState, FC, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Layout, Menu } from 'antd';
import type { MenuProps } from 'antd';
import { setLanguage } from '@/utils/languageSync';
import { PATHS } from '@/router/paths';
import useThemeStore from '@/store/useThemeStore';
import useIsMobile from '@/hooks/useIsMobile';
import SvgIcon from '@/components/SvgIcon';
import styles from './index.module.scss';

import LogoPD from '@/assets/images/menu/P-D.png';
import LogoPL from '@/assets/images/menu/P-L.png';

// 导入国旗图标
import EnglishLight from '@/assets/svg/menu/LIGHT/English.svg';
import EnglishDark from '@/assets/svg/menu/DARK/English.svg';
import FrenchLight from '@/assets/svg/menu/LIGHT/French.svg';
import FrenchDark from '@/assets/svg/menu/DARK/French.svg';

const IconFont: FC<{ type: string; className?: string }> = ({ type, className }) => (
  <svg className={`${styles.iconFont} ${className || ''}`} aria-hidden="true">
    <use xlinkHref={`#icon-${type}`} />
  </svg>
);

const { Sider } = Layout;

type MenuItem = Required<MenuProps>['items'][number];

type Translate = (key: string) => string;

type Language = 'en' | 'fr';

type LanguageMeta = {
  code: Language;
  name: string;
  flag: string;
};

const LANGUAGE_META: Record<Language, LanguageMeta> = {
  en: { code: 'en', name: 'English', flag: 'English' },
  fr: { code: 'fr', name: 'Français', flag: 'French' },
};

const normalizeLanguage = (lang: string): Language => {
  const code = lang.split('-')[0];
  return code === 'fr' ? 'fr' : 'en';
};

const createLanguageIcon = (language: LanguageMeta, isDarkMode: boolean) => {
  const iconMap = {
    English: { light: EnglishLight, dark: EnglishDark },
    French: { light: FrenchLight, dark: FrenchDark },
  };

  const iconSrc = isDarkMode ? iconMap[language.flag as 'English' | 'French'].dark : iconMap[language.flag as 'English' | 'French'].light;
  return <img src={iconSrc} alt={language.name} className={styles.languageFlag} style={{ width: '20px', height: '20px' }} />;
};

const readSidebarLayoutFromCssVars = () => {
  const rootStyle = window.getComputedStyle(document.documentElement);
  const collapsed = rootStyle.getPropertyValue('--sidebar-width-collapsed').trim();
  const expanded = rootStyle.getPropertyValue('--sidebar-width-expanded').trim();
  const menuInlineIndent = rootStyle.getPropertyValue('--sidebar-menu-inline-indent').trim();
  return {
    collapsedWidth: Number.parseFloat(collapsed) || 68,
    width: Number.parseFloat(expanded) || 280,
    menuInlineIndent: Number.parseFloat(menuInlineIndent) || 48,
  };
};

const MENU_ICON_MAP = {
  dashboard: 'dashboard',
  documentAnalysis: 'Document-Analysis',
  documentGeneration: 'Document-Generation',
  interviewSimulation: 'Interview-Simulation',
} as const;

const createMenuItems = (t: Translate, currentPath: string, isDarkMode: boolean): MenuItem[] => {
  const activeIconPath = isDarkMode ? 'menu-DARK' : 'menu-LIGHT';
  const isActive = (path: string) => currentPath === path || currentPath.startsWith(path + '/');

  return [
    {
      key: PATHS.dashboard,
      icon: isActive(PATHS.dashboard) ? <SvgIcon name={MENU_ICON_MAP.dashboard} path={activeIconPath} size={20} className={styles.iconFontGradient} /> : <IconFont type="dashboard" />,
      label: t('menu.dashboard'),
    },
    {
      key: PATHS.documentAnalysis.root,
      icon: isActive(PATHS.documentAnalysis.root) ? (
        <SvgIcon name={MENU_ICON_MAP.documentAnalysis} path={activeIconPath} size={20} className={styles.iconFontGradient} />
      ) : (
        <IconFont type="a-DocumentAnalysis" />
      ),
      label: t('menu.documentAnalysis'),
      children: [
        {
          key: PATHS.documentAnalysis.cv,
          label: t('menu.cv'),
        },
        {
          key: PATHS.documentAnalysis.coverLetter,
          label: t('menu.coverLetter'),
        },
        {
          key: PATHS.documentAnalysis.history,
          label: t('menu.history'),
        },
      ],
    },
    {
      key: PATHS.documentGeneration.root,
      icon: isActive(PATHS.documentGeneration.root) ? (
        <SvgIcon name={MENU_ICON_MAP.documentGeneration} path={activeIconPath} size={20} className={styles.iconFontGradient} />
      ) : (
        <IconFont type="a-DocumentGeneration" />
      ),
      label: t('menu.documentGeneration'),
      children: [
        {
          key: PATHS.documentGeneration.coverLetter,
          label: t('menu.coverLetter'),
        },
        {
          key: PATHS.documentGeneration.history,
          label: t('menu.history'),
        },
      ],
    },
    {
      key: PATHS.interviewSimulation.root,
      icon: isActive(PATHS.interviewSimulation.root) ? (
        <SvgIcon name={MENU_ICON_MAP.interviewSimulation} path={activeIconPath} size={20} className={styles.iconFontGradient} />
      ) : (
        <IconFont type="a-InterviewSimulation" />
      ),
      label: t('menu.interviewSimulation'),
      children: [
        {
          key: PATHS.interviewSimulation.start,
          label: t('menu.startInterview'),
        },
        {
          key: PATHS.interviewSimulation.history,
          label: t('menu.interviewHistory'),
        },
      ],
    },
  ];
};

const BOTTOM_ICON_MAP = {
  profile: 'profile',
  subscription: 'Subscription',
  contactUs: 'Contact-Us',
} as const;

const createBottomItems = (t: Translate, language: LanguageMeta, currentPath: string, isDarkMode: boolean): MenuItem[] => {
  const activeIconPath = isDarkMode ? 'menu-DARK' : 'menu-LIGHT';
  const isActive = (path: string) => currentPath === path || currentPath.startsWith(path + '/');

  return [
    {
      key: 'language',
      icon: createLanguageIcon(language, isDarkMode),
      label: language.name,
    },
    {
      key: PATHS.subscription,
      icon: isActive(PATHS.subscription) ? <SvgIcon name={BOTTOM_ICON_MAP.subscription} path={activeIconPath} size={20} className={styles.iconFontGradient} /> : <IconFont type="Subscription" />,
      label: t('menu.subscription'),
    },
    {
      key: PATHS.contactUs,
      icon: isActive(PATHS.contactUs) ? <SvgIcon name={BOTTOM_ICON_MAP.contactUs} path={activeIconPath} size={20} className={styles.iconFontGradient} /> : <IconFont type="a-ContactUs" />,
      label: t('menu.contactUs'),
    },
    {
      key: PATHS.profile,
      icon: isActive(PATHS.profile) ? <SvgIcon name={BOTTOM_ICON_MAP.profile} path={activeIconPath} size={20} className={styles.iconFontGradient} /> : <IconFont type="profile" />,
      label: t('menu.profile'),
    },
  ];
};

const Sidebar: FC = () => {
  const isMobile = useIsMobile();
  const [collapsed, setCollapsed] = useState(!isMobile);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [canHover, setCanHover] = useState(true);
  const [sidebarLayout, setSidebarLayout] = useState(() => ({ collapsedWidth: 68, width: 280, menuInlineIndent: 48 }));
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { isDarkMode } = useThemeStore();
  const currentLanguage = LANGUAGE_META[normalizeLanguage(i18n.language || 'en')];

  const logoSmall = isDarkMode ? LogoPD : LogoPL;
  const logoFullSrc = isDarkMode ? '/logo/THEPATCH-D.png' : '/logo/THEPATCH-L.png';
  const logoFullSrcSet = isDarkMode ? '/logo/THEPATCH-D.png 1x, /logo/THEPATCH-D@2x.png 2x' : '/logo/THEPATCH-L.png 1x, /logo/THEPATCH-L@2x.png 2x';

  const menuItems = createMenuItems(t, location.pathname, isDarkMode);
  const bottomItems = createBottomItems(t, currentLanguage, location.pathname, isDarkMode);
  const shouldUseHover = !isMobile && canHover;
  const shouldAutoCollapse = !shouldUseHover;
  const isHistoryRoute = [PATHS.documentAnalysis.history, PATHS.documentGeneration.history, PATHS.interviewSimulation.history].some((path) => path === location.pathname);
  const menuDefaultPaths: Record<string, string> = {
    [PATHS.documentAnalysis.root]: PATHS.documentAnalysis.history,
    [PATHS.documentGeneration.root]: PATHS.documentGeneration.history,
    [PATHS.interviewSimulation.root]: PATHS.interviewSimulation.start,
  };

  useEffect(() => {
    const update = () => setSidebarLayout(readSidebarLayoutFromCssVars());
    update();

    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mediaQuery = window.matchMedia('(hover: hover)');
    const update = () => setCanHover(mediaQuery.matches);
    update();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', update);
      return () => mediaQuery.removeEventListener('change', update);
    }
    mediaQuery.addListener(update);
    return () => mediaQuery.removeListener(update);
  }, []);

  // 监听设备类型变化，自动调整侧边栏状态
  useEffect(() => {
    setCollapsed(!isMobile);
  }, [isMobile]);

  // 在无 hover 设备下，根据当前路径自动展开对应的子菜单
  useEffect(() => {
    if (shouldAutoCollapse && !collapsed) {
      const groupKey = getGroupKeyByPathname(location.pathname);
      if (groupKey) {
        setOpenKeys((prev) => {
          // 避免不必要的状态更新
          if (prev.length === 1 && prev[0] === groupKey) {
            return prev;
          }
          return [groupKey];
        });
      }
    }
  }, [shouldAutoCollapse, collapsed, location.pathname]);

  useEffect(() => {
    if (!shouldAutoCollapse || collapsed || !isHistoryRoute) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Element | null;
      if (!target) return;
      if (target.closest(`.${styles.sidebar}`)) return;

      setCollapsed(true);
      setOpenKeys([]);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [shouldAutoCollapse, collapsed, isHistoryRoute]);

  const getGroupKeyByPathname = (pathname: string): string | null => {
    if (pathname.startsWith(PATHS.documentAnalysis.root)) return PATHS.documentAnalysis.root;
    if (pathname.startsWith(PATHS.documentGeneration.root)) return PATHS.documentGeneration.root;
    if (pathname.startsWith(PATHS.interviewSimulation.root)) return PATHS.interviewSimulation.root;
    return null;
  };

  const handleMouseEnter = () => {
    setCollapsed(false);
    setOpenKeys((prev) => {
      if (prev.length > 0) return prev;
      const groupKey = getGroupKeyByPathname(location.pathname);
      return groupKey ? [groupKey] : [];
    });
  };

  const handleMouseLeave = () => {
    setCollapsed(true);
    setOpenKeys([]);
  };

  const handleOpenChange: MenuProps['onOpenChange'] = (keys) => {
    const nextKeys = keys as string[];
    if (shouldAutoCollapse && collapsed && nextKeys.length > 0) {
      const openedKey = nextKeys.find((key) => !openKeys.includes(key));
      setCollapsed(false);
      // 确保只保留一个 key，符合手风琴逻辑
      setOpenKeys(openedKey ? [openedKey] : nextKeys.slice(0, 1));
      const targetPath = openedKey ? menuDefaultPaths[openedKey] : undefined;
      if (targetPath) {
        navigate(targetPath, { state: { source: 'sidebar' } });
      }
      return;
    }

    // 手风琴逻辑：只允许一个子菜单展开
    const latestOpenKey = nextKeys.find((key) => !openKeys.includes(key));
    if (latestOpenKey) {
      // 有新打开的子菜单，只保留这一个
      setOpenKeys([latestOpenKey]);
    } else {
      // 没有新打开的，说明是关闭操作
      setOpenKeys(nextKeys);
    }
  };

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key.startsWith('/')) {
      // 直接导航到菜单项路径（子菜单标题点击触发 onOpenChange，不会到这里）
      navigate(key, { state: { source: 'sidebar' } });
      // 在无 hover 设备下，点击菜单项后折叠侧边栏
      if (shouldAutoCollapse) {
        setCollapsed(true);
        setOpenKeys([]);
      }
    }
  };

  const handleBottomMenuClick: MenuProps['onClick'] = ({ key }) => {
    switch (key) {
      case 'language':
        setLanguage(currentLanguage.code === 'en' ? 'fr' : 'en');
        break;
      default:
        if (key.startsWith('/')) {
          navigate(key, { state: { source: 'sidebar' } });
          if (shouldAutoCollapse) {
            setCollapsed(true);
            setOpenKeys([]);
          }
        }
    }
  };

  return (
    <Sider
      className={styles.sidebar}
      collapsed={collapsed}
      collapsedWidth={sidebarLayout.collapsedWidth}
      width={sidebarLayout.width}
      onMouseEnter={shouldUseHover ? handleMouseEnter : undefined}
      onMouseLeave={shouldUseHover ? handleMouseLeave : undefined}
    >
      <div className={styles.logo}>
        <img src={logoSmall} alt="The Patch" className={`${styles.logoSmall} ${collapsed ? styles.visible : styles.hidden}`} />
        <img src={logoFullSrc} srcSet={logoFullSrcSet} alt="The Patch" className={`${styles.logoFull} ${collapsed ? styles.hidden : styles.visible}`} />
      </div>

      <Menu
        className={styles.menu}
        mode="inline"
        inlineIndent={sidebarLayout.menuInlineIndent}
        selectedKeys={[location.pathname]}
        openKeys={collapsed ? [] : openKeys}
        onOpenChange={handleOpenChange}
        items={menuItems}
        onClick={handleMenuClick}
        triggerSubMenuAction={shouldAutoCollapse ? 'click' : 'hover'}
      />

      <div className={styles.bottomSection}>
        <Menu className={styles.bottomMenu} mode="inline" inlineIndent={sidebarLayout.menuInlineIndent} selectable={false} items={bottomItems} onClick={handleBottomMenuClick} />
      </div>
    </Sider>
  );
};

export default Sidebar;
